import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Upload, X } from "lucide-react";
import { ROOM_TYPES, COMMERCIAL_SPACE_TYPES, MEASUREMENT_UNITS, PROPERTY_SUBTYPES } from "@/data/propertyFormConstants";

const CAPIN_LOGO_URL = "https://customer-assets.emergentagent.com/job_realtor-dash-3/artifacts/r98q2q3d_Capin%20Realty%20Logo.jpg";

export const MeasurementsTab = ({
  formData,
  setFormData,
  planFiles,
  existingPlanUrls,
  handlePlanImageChange,
  removePlanFile,
  addRoomMeasurement,
  removeRoomMeasurement,
  updateRoomMeasurement,
  addOfficeRoomMeasurement,
  removeOfficeRoomMeasurement,
  updateOfficeRoomMeasurement,
  updatePlotDimensions,
}) => {
  return (
<div className="space-y-8">
  {/* Persistent Plan Image Uploads - Always visible at top */}
  <div>
    <Label className="text-lg font-semibold mb-2 block">Property Plans & Maps</Label>
    <p className="text-sm text-slate-500 mb-4">Upload floor plan, project plan, or location map for the property (optional, one-time upload).</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {[
        { key: 'floor_plan_image', label: 'Floor Plan' },
        { key: 'project_plan_image', label: 'Project Plan' },
        { key: 'location_image', label: 'Location Map' },
      ].map(({ key, label }) => (
        <div key={key} data-testid={`plan-upload-${key}`}>
          <Label className="text-sm font-medium mb-2 block">{label}</Label>
          {planFiles[key] ? (
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-[#c9a227] shadow-md group">
              <img src={URL.createObjectURL(planFiles[key])} alt={label} className="w-full h-full object-cover" />
              <button type="button" onClick={() => removePlanFile(key)} className="absolute top-2 right-2 bg-[#0f2744] text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-xs p-2">{label}</div>
            </div>
          ) : existingPlanUrls[key] ? (
            <div className="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-[#c9a227] shadow-md group">
              <img src={existingPlanUrls[key]} alt={label} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-xs p-2">{label}</div>
            </div>
          ) : (
            <label htmlFor={`plan-${key}`} className="flex flex-col items-center justify-center aspect-[4/3] rounded-lg border-2 border-dashed border-[#0f2744]/30 hover:border-[#c9a227] hover:bg-[#f0f4f8] cursor-pointer transition-all">
              <img src={CAPIN_LOGO_URL} alt="CAPIN Realty" className="w-14 h-14 object-contain opacity-30 mb-2 rounded" />
              <Upload className="h-5 w-5 text-slate-400 mb-1" />
              <p className="text-xs text-slate-500 font-medium">{label}</p>
              <p className="text-[10px] text-slate-400">Optional - Max 5MB</p>
              <input id={`plan-${key}`} type="file" accept="image/*" onChange={(e) => handlePlanImageChange(e, key)} className="hidden" />
            </label>
          )}
        </div>
      ))}
    </div>
  </div>

  {/* Room Measurements Section - For Residential */}
  {formData.property_type === 'residential' && (
    <div>
      <Label className="text-lg font-semibold mb-4 block">Room Measurements</Label>
      
      {formData.room_measurements.length === 0 ? (
        <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <p className="text-slate-500 text-sm">No room measurements added yet.</p>
          <p className="text-xs text-slate-400 mt-1">Click "+ Add Room" to add detailed room sizes.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.room_measurements.map((room, index) => {
            const sameTypeBefore = formData.room_measurements.slice(0, index).filter(r => r.room_type === room.room_type).length;
            const sameTypeTotal = formData.room_measurements.filter(r => r.room_type === room.room_type).length;
            const displayName = sameTypeTotal > 1 ? `${room.room_type} ${sameTypeBefore + 1}` : room.room_type;
            return (
            <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-slate-700">{displayName}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRoomMeasurement(index)}
                  className="text-[#0f2744] hover:text-[#1e3a5f] hover:bg-[#0f2744]/10"
                  data-testid={`remove-room-${index}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label>Room Type</Label>
                  <select
                    value={room.room_type}
                    onChange={(e) => updateRoomMeasurement(index, 'room_type', e.target.value)}
                    className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                  >
                    {ROOM_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Length (ft)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="12"
                    value={room.length}
                    onChange={(e) => updateRoomMeasurement(index, 'length', e.target.value)}
                    className="mt-1 h-10 rounded-sm"
                  />
                </div>
                <div>
                  <Label>Width (ft)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="10"
                    value={room.width}
                    onChange={(e) => updateRoomMeasurement(index, 'width', e.target.value)}
                    className="mt-1 h-10 rounded-sm"
                  />
                </div>
                <div>
                  <Label>Area</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Auto"
                    value={room.area}
                    onChange={(e) => updateRoomMeasurement(index, 'area', e.target.value)}
                    className="mt-1 h-10 rounded-sm bg-slate-100"
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <select
                    value={room.unit}
                    onChange={(e) => updateRoomMeasurement(index, 'unit', e.target.value)}
                    className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                  >
                    {MEASUREMENT_UNITS.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Add Room button - below all room cards */}
      <div className="mt-4 flex justify-end">
        <Button
          type="button"
          onClick={addRoomMeasurement}
          className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
          data-testid="add-room-btn"
        >
          + Add Room
        </Button>
      </div>
    </div>
  )}

  {/* Office Room Measurements Section - For Commercial - Dynamic based on subtype */}
  {formData.property_type === 'commercial' && (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <Label className="text-lg font-semibold">
            {formData.property_subtype 
              ? `${PROPERTY_SUBTYPES.commercial.find(s => s.value === formData.property_subtype)?.label || 'Commercial'} Space Measurements`
              : 'Commercial Space Measurements'}
          </Label>
          {formData.property_subtype && (
            <p className="text-sm text-slate-500 mt-1">
              Add detailed measurements for your {PROPERTY_SUBTYPES.commercial.find(s => s.value === formData.property_subtype)?.label.toLowerCase() || 'property'}
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={addOfficeRoomMeasurement}
          className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
          data-testid="add-office-room-btn"
        >
          + Add Space
        </Button>
      </div>
      
      {!formData.property_subtype && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-amber-800">
            <strong>Tip:</strong> Select a property category in the Basic Details tab to see relevant space types for your property.
          </p>
        </div>
      )}
      
      {formData.office_room_measurements.length === 0 ? (
        <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <p className="text-slate-500 text-sm">No space measurements added yet.</p>
          <p className="text-xs text-slate-400 mt-1">Click "Add Space" to add detailed area sizes.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.office_room_measurements.map((room, index) => (
            <div key={index} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-slate-700">Space {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOfficeRoomMeasurement(index)}
                  className="text-[#0f2744] hover:text-[#1e3a5f] hover:bg-[#0f2744]/10"
                  data-testid={`remove-office-room-${index}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label>Space Type</Label>
                  <select
                    value={room.room_type}
                    onChange={(e) => updateOfficeRoomMeasurement(index, 'room_type', e.target.value)}
                    className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                  >
                    {/* Use subtype-specific space types if available, otherwise default to office */}
                    {(COMMERCIAL_SPACE_TYPES[formData.property_subtype] || COMMERCIAL_SPACE_TYPES.office_space).map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Length (ft)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="15"
                    value={room.length}
                    onChange={(e) => updateOfficeRoomMeasurement(index, 'length', e.target.value)}
                    className="mt-1 h-10 rounded-sm"
                  />
                </div>
                <div>
                  <Label>Width (ft)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="12"
                    value={room.width}
                    onChange={(e) => updateOfficeRoomMeasurement(index, 'width', e.target.value)}
                    className="mt-1 h-10 rounded-sm"
                  />
                </div>
                <div>
                  <Label>Area</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Auto"
                    value={room.area}
                    onChange={(e) => updateOfficeRoomMeasurement(index, 'area', e.target.value)}
                    className="mt-1 h-10 rounded-sm bg-slate-100"
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <select
                    value={room.unit}
                    onChange={(e) => updateOfficeRoomMeasurement(index, 'unit', e.target.value)}
                    className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                  >
                    {MEASUREMENT_UNITS.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )}

  {/* Plot Dimensions Section */}
  <div>
    <Label className="text-lg font-semibold mb-4 block">Plot Dimensions</Label>
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <Label>Length</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="50"
            value={formData.plot_dimensions.length}
            onChange={(e) => updatePlotDimensions('length', e.target.value)}
            className="mt-1 h-10 rounded-sm"
          />
        </div>
        <div>
          <Label>Width</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="40"
            value={formData.plot_dimensions.width}
            onChange={(e) => updatePlotDimensions('width', e.target.value)}
            className="mt-1 h-10 rounded-sm"
          />
        </div>
        <div>
          <Label>Total Area</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="Auto"
            value={formData.plot_dimensions.area}
            onChange={(e) => updatePlotDimensions('area', e.target.value)}
            className="mt-1 h-10 rounded-sm bg-slate-100"
          />
        </div>
        <div>
          <Label>Unit</Label>
          <select
            value={formData.plot_dimensions.unit}
            onChange={(e) => updatePlotDimensions('unit', e.target.value)}
            className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
          >
            {MEASUREMENT_UNITS.map(unit => (
              <option key={unit.value} value={unit.value}>{unit.label}</option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-3">Enter plot length and width to auto-calculate area, or enter area directly.</p>
    </div>
  </div>

  {/* Room Floor Plan & 3D Image Uploads */}
  <div className="border-t border-slate-200 pt-6">
    <Label className="text-lg font-semibold mb-4 block">Room Floor Plan & 3D View</Label>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {[
        { key: 'room_floor_plan_image', label: 'Room Floor Plan' },
        { key: 'three_d_image', label: '3D Image / View' },
      ].map(({ key, label }) => (
        <div key={key} data-testid={`plan-upload-${key}`}>
          <Label className="text-sm font-medium mb-2 block">{label}</Label>
          {planFiles[key] ? (
            <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-[#c9a227] shadow-md group">
              <img src={URL.createObjectURL(planFiles[key])} alt={label} className="w-full h-full object-cover" />
              <button type="button" onClick={() => removePlanFile(key)} className="absolute top-2 right-2 bg-[#0f2744] text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                <X className="h-4 w-4" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-xs p-2">{label}</div>
            </div>
          ) : existingPlanUrls[key] ? (
            <div className="relative aspect-video rounded-lg overflow-hidden border-2 border-[#c9a227] shadow-md group">
              <img src={existingPlanUrls[key]} alt={label} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white text-xs p-2">{label}</div>
            </div>
          ) : (
            <label htmlFor={`plan-${key}`} className="flex flex-col items-center justify-center aspect-video rounded-lg border-2 border-dashed border-[#0f2744]/30 hover:border-[#c9a227] hover:bg-[#f0f4f8] cursor-pointer transition-all">
              <img src={CAPIN_LOGO_URL} alt="CAPIN Realty" className="w-16 h-16 object-contain opacity-30 mb-2 rounded" />
              <Upload className="h-5 w-5 text-slate-400 mb-1" />
              <p className="text-xs text-slate-500 font-medium">{label}</p>
              <p className="text-[10px] text-slate-400">Optional - Max 5MB</p>
              <input id={`plan-${key}`} type="file" accept="image/*" onChange={(e) => handlePlanImageChange(e, key)} className="hidden" />
            </label>
          )}
        </div>
      ))}
    </div>
    <p className="text-xs text-slate-500 mt-3">Upload room floor plan and 3D images for a better property presentation.</p>
  </div>
</div>
  );
};
